import * as React from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import type { LeadStatsMetricDto, LeadStatsPipelineDto, LeadStatsSettingsDto } from '@lib/dto/lead-stats.dto'
import { putLeadStatsSettingsInputSchema } from '@lib/input/lead-stats.input'
import { LEAD_STATS_METRIC_KINDS, leadStatsMetricKindSchema, type LeadStatsMetricKind } from '@lib/enums/lead-stats-metric-kind.enum'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { Field } from '@/components/ui/field'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import { Select } from '@/components/ui/select'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSaveLeadStatsSettings } from '@/hooks/useLeadStats'
import { AppointmentsDefinitionFields, LeadsDefinitionFields, NameField } from './DefinitionFields'

const KIND_LABEL: Record<LeadStatsMetricKind, string> = {
  leads: 'Couples in the pipeline',
  appointments: 'Appointments on calendars',
  conversion: 'A conversion between numbers',
}

/** The kind a select's value names; anything else is a count of couples. */
function kindOf(value: string): LeadStatsMetricKind {
  const parsed = leadStatsMetricKindSchema.safeParse(value)
  return parsed.success ? parsed.data : 'leads'
}

/** What a validation problem means, in the words of the page. */
function plainIssue(issue: { code: string; message: string; path: readonly PropertyKey[] }): string {
  if (issue.code === 'custom') return issue.message
  const path = issue.path.map(String)
  if (path.includes('name')) return 'Give every pipeline and number a name, of up to 60 characters.'
  if (path.includes('ghlPipelineNames')) return 'Choose at least one GoHighLevel pipeline for each pipeline.'
  if (path.includes('calendars')) return 'Choose at least one calendar for each number that counts appointments.'
  if (path.includes('numerator') || path.includes('denominator')) return 'Choose the numbers each conversion divides, on both sides.'
  return 'Something here isn’t complete.'
}

/** A fresh number of a kind, as the editor starts it. */
function blankMetric(id: string, name: string, kind: LeadStatsMetricKind): LeadStatsMetricDto {
  if (kind === 'leads') return { id, name, kind, definition: { stages: [], results: [], tagsAny: [], tagsNone: [], sources: [] } }
  if (kind === 'appointments') return { id, name, kind, definition: { calendars: [], statuses: [], countBy: 'booked' } }
  return { id, name, kind, definition: { numerator: [], denominator: [] } }
}

function move<T>(list: readonly T[], index: number, by: -1 | 1): T[] {
  const to = index + by
  if (to < 0 || to >= list.length) return [...list]
  const next = [...list]
  const [item] = next.splice(index, 1)
  if (item !== undefined) next.splice(to, 0, item)
  return next
}

/**
 * The company's lead stats definitions (docs/gohighlevel-stats.spec.md,
 * "Pipelines" and "Numbers"): its named pipelines, its numbers and conversions in
 * the order every venue shows them, and the email. Saved whole.
 */
export function LeadStatsSettingsEditor({ settings }: { settings: LeadStatsSettingsDto }) {
  const { toast } = useToast()
  const save = useSaveLeadStatsSettings()
  const [pipelines, setPipelines] = React.useState<LeadStatsPipelineDto[]>(settings.pipelines)
  const [metrics, setMetrics] = React.useState<LeadStatsMetricDto[]>(settings.metrics)
  const [emailOff, setEmailOff] = React.useState(settings.emailOff)
  const [error, setError] = React.useState<string | null>(null)
  const choices = settings.choices
  const counted = metrics.filter((m) => m.kind !== 'conversion')

  function updateMetric(index: number, metric: LeadStatsMetricDto): void {
    setMetrics((all) => all.map((m, i) => (i === index ? metric : m)))
  }

  function removeMetric(index: number): void {
    setMetrics((all) => {
      const gone = all[index]
      return all
        .filter((_, i) => i !== index)
        .map((m) =>
          m.kind === 'conversion' && gone !== undefined
            ? {
                ...m,
                definition: {
                  numerator: m.definition.numerator.filter((id) => id !== gone.id),
                  denominator: m.definition.denominator.filter((id) => id !== gone.id),
                },
              }
            : m,
        )
    })
  }

  function handleSave(): void {
    const parsed = putLeadStatsSettingsInputSchema.safeParse({ pipelines, metrics, emailOff })
    if (!parsed.success) {
      const issue = parsed.error.issues[0]
      setError(issue === undefined ? 'Something here isn’t complete.' : plainIssue(issue))
      return
    }
    setError(null)
    save.mutate(parsed.data, {
      onSuccess: () => toast('Saved. Every venue’s numbers now count this way.'),
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  return (
    <div className="space-y-6">
      {choices.read ? null : (
        <Callout tone="neutral" title="Nothing has been read from GoHighLevel yet">
          A venue’s leads are read overnight once it has a connection. The stages, tags, sources and calendars to choose from appear after
          that.
        </Callout>
      )}

      <section className="space-y-3">
        <h3 className="m-0 text-sm font-semibold text-ink">Pipelines</h3>
        <p className="m-0 text-sm text-ink-muted">
          Name each pipeline the way your venues talk about it, and say which GoHighLevel pipelines make it up. Each is shown on its own.
        </p>
        {pipelines.map((pipeline, index) => (
          <Card
            key={pipeline.id}
            className="grid items-end gap-3 p-3 md:grid-cols-[14rem_1fr_auto]"
          >
            <Field label="Name" htmlFor={`pipeline-${pipeline.id}-name`}>
              <Input
                id={`pipeline-${pipeline.id}-name`}
                value={pipeline.name}
                onChange={(e) => setPipelines((all) => all.map((p, i) => (i === index ? { ...p, name: e.target.value } : p)))}
                placeholder="Weddings"
              />
            </Field>
            <NameField
              id={`pipeline-${pipeline.id}-ghl`}
              label="GoHighLevel pipelines"
              available={choices.pipelines.map((p) => p.name)}
              chosen={pipeline.ghlPipelineNames}
              onChange={(ghlPipelineNames) => setPipelines((all) => all.map((p, i) => (i === index ? { ...p, ghlPipelineNames } : p)))}
              placeholder="Choose pipelines"
            />
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Remove ${pipeline.name || 'this pipeline'}`}
              onClick={() => setPipelines((all) => all.filter((_, i) => i !== index))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </Card>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPipelines((all) => [...all, { id: crypto.randomUUID(), name: '', ghlPipelineNames: [] }])}
        >
          <Plus className="h-3.5 w-3.5" />
          Add pipeline
        </Button>
      </section>

      <section className="space-y-3">
        <h3 className="m-0 text-sm font-semibold text-ink">Numbers</h3>
        <p className="m-0 text-sm text-ink-muted">
          Every venue shows these, in this order. A number left without stages, results, tags or sources counts every lead.
        </p>
        {metrics.map((metric, index) => (
          <Card key={metric.id} className="space-y-3 p-3">
            <div className="grid items-end gap-3 md:grid-cols-[14rem_16rem_1fr]">
              <Field label="Name" htmlFor={`metric-${metric.id}-name`}>
                <Input
                  id={`metric-${metric.id}-name`}
                  value={metric.name}
                  onChange={(e) => updateMetric(index, { ...metric, name: e.target.value })}
                  placeholder="Tours booked"
                />
              </Field>
              <Field label="Counts" htmlFor={`metric-${metric.id}-kind`}>
                <Select
                  id={`metric-${metric.id}-kind`}
                  value={metric.kind}
                  onChange={(e) => updateMetric(index, blankMetric(metric.id, metric.name, kindOf(e.target.value)))}
                >
                  {LEAD_STATS_METRIC_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {KIND_LABEL[kind]}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Move up"
                  disabled={index === 0}
                  onClick={() => setMetrics((all) => move(all, index, -1))}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Move down"
                  disabled={index === metrics.length - 1}
                  onClick={() => setMetrics((all) => move(all, index, 1))}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" aria-label={`Remove ${metric.name || 'this number'}`} onClick={() => removeMetric(index)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {metric.kind === 'leads' ? (
              <LeadsDefinitionFields
                idPrefix={`metric-${metric.id}`}
                definition={metric.definition}
                choices={choices}
                onChange={(definition) => updateMetric(index, { ...metric, definition })}
              />
            ) : null}
            {metric.kind === 'appointments' ? (
              <AppointmentsDefinitionFields
                idPrefix={`metric-${metric.id}`}
                definition={metric.definition}
                choices={choices}
                onChange={(definition) => updateMetric(index, { ...metric, definition })}
              />
            ) : null}
            {metric.kind === 'conversion' ? (
              <div className="grid gap-3 md:grid-cols-2">
                {(['numerator', 'denominator'] as const).map((side) => (
                  <Field
                    key={side}
                    label={side === 'numerator' ? 'Of these numbers, added up' : 'Divided by these, added up'}
                    htmlFor={`metric-${metric.id}-${side}`}
                  >
                    <MultiSelect
                      id={`metric-${metric.id}-${side}`}
                      options={counted.map((m) => ({ value: m.id, label: m.name || 'Unnamed number' }))}
                      selected={metric.definition[side]}
                      onToggle={(id) => {
                        const list = metric.definition[side]
                        const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id]
                        updateMetric(index, { ...metric, definition: { ...metric.definition, [side]: next } })
                      }}
                      placeholder="Choose numbers"
                    />
                  </Field>
                ))}
              </div>
            ) : null}
          </Card>
        ))}
        <Button variant="outline" size="sm" onClick={() => setMetrics((all) => [...all, blankMetric(crypto.randomUUID(), '', 'leads')])}>
          <Plus className="h-3.5 w-3.5" />
          Add number
        </Button>
      </section>

      <section className="space-y-2">
        <h3 className="m-0 text-sm font-semibold text-ink">Email</h3>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={!emailOff} onChange={(e) => setEmailOff(!e.target.checked)} />
          Email each venue’s admins its numbers, and organization admins every venue’s, after each two-week period
        </label>
      </section>

      {error === null ? null : <FormError>{error}</FormError>}
      <FormActions>
        <Button
          variant="outline"
          onClick={() => {
            setPipelines(settings.pipelines)
            setMetrics(settings.metrics)
            setEmailOff(settings.emailOff)
            setError(null)
          }}
          disabled={save.isPending}
        >
          Discard changes
        </Button>
        <Button onClick={handleSave} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save numbers'}
        </Button>
      </FormActions>
    </div>
  )
}
